import { useCallback, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * The label for an icon in the collapsed rail.
 *
 * It is portalled to <body> rather than positioned inside the rail because the
 * rail is a scrolling column: a tooltip rendered inside it is clipped at the
 * rail's edge, which is exactly where the tooltip needs to appear.
 *
 * The rail sits on the reading-start edge, so the label opens toward the
 * content — to the rail's left in Arabic, to its right in English. The anchor
 * is measured rather than assumed, because the rail moves with the direction.
 */
export function SidebarTooltip({
  label,
  children,
  isRTL = true,
}: {
  label: string;
  children: ReactNode;
  isRTL?: boolean;
}) {
  const anchor = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const show = useCallback(() => {
    if (!anchor.current) return;
    const rect = anchor.current.getBoundingClientRect();
    setPos({ top: rect.top + rect.height / 2, left: isRTL ? rect.left : rect.right });
  }, [isRTL]);

  const hide = useCallback(() => setPos(null), []);

  return (
    <div
      ref={anchor}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      className="relative flex justify-center"
    >
      {children}
      {pos
        ? createPortal(
            <div
              role="tooltip"
              className="pointer-events-none fixed z-[100]"
              style={{
                top: pos.top,
                ...(isRTL
                  ? { right: window.innerWidth - pos.left + 12 }
                  : { left: pos.left + 12 }),
                transform: 'translateY(-50%)',
              }}
            >
              <span className="whitespace-nowrap rounded-md bg-ink px-3 py-1.5 text-xs font-medium text-white shadow-lg">
                {label}
              </span>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
